(ns ductile.ui.load-builder-v2
  (:require [applied-science.js-interop :as j]
            [cljs.reader :as reader]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.components.columns :as columns]
            [ductile.ui.components.filters :as filters]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.http :as http]
            [ductile.ui.load-builder :as lb1]
            [ductile.ui.load-builder-v2.effects :as effects]
            [ductile.ui.load-builder-v2.hooks :as lbhooks]
            [ductile.ui.load-builder-v2.panels :as panels]
            [ductile.ui.load-builder-v2.protocol :as protocol]
            [ductile.ui.load-builder-v2.selectors :as selectors]
            [ductile.ui.load-builder.overview-map :as overview-map]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.mapbox :as mapbox]
            [ductile.ui.paneling :as paneling]
            [ductile.ui.routes :as routes]
            [ductile.ui.transport :as transport]
            [ductile.ui.util :as ui-util]
            [nextjournal.clerk.render.hooks :as hooks]))

(defonce panel-ids [:map :vehicles :transports :staging])

(def panel-titles
  {:vehicles {:title (tr "Vehicles")}
   :transports {:title (tr "Transports")}
   :staging {:title (tr "Staging")}
   :map {:title (tr "Map")}})

(def vehicles-col-storage-key :ductile.ui.load-builder/dispatchable-vehicles-col-settings)
(def views-storage-key :ductile.ui.load-builder-v2/views)

(def default-vehicles-column-order
  [:vehicle/vin
   :fahrzeug/cardinal-direction
   :fahrzeug/priority
   :fahrzeug/priorities
   :fahrzeug/labels
   :fahrzeug/expected-customer-delivery-datetime
   :fahrzeug/call-off-datetime
   :dispo/available-datetime
   :exit/expected-datetime
   :load/id
   [:transport/origin lb1/ordered-address-fields]
   [:transport/destination lb1/ordered-address-fields]
   :make/name
   :vehicle/model
   :contract/name
   :vehicle/weight
   :fahrzeug/parking-spot])

(defn discovered-vehicle-columns [vehicle]
  (into #{}
        (mapcat (fn [[k v]]
                  (cond
                    (= k :ductile/id)
                    []

                    (map? v)
                    (->> (keys v)
                         (remove #{:ductile/id})
                         (map (fn [child] [k child])))

                    :else
                    [k])))
        vehicle))

(defn vehicles-available-columns [rows]
  (let [default-expanded (->> default-vehicles-column-order
                              (mapcat columns/expand-col)
                              (map :attr)
                              set)
        discovered (->> rows
                        (mapcat discovered-vehicle-columns)
                        set)
        extra-columns (->> (set/difference discovered default-expanded)
                           (sort-by pr-str)
                           vec)]
    (vec (concat default-vehicles-column-order extra-columns))))

(def vehicles-filters-key ::vehicles-filters)
(def vehicles-filters-draft-key ::vehicles-filters-draft)
(def vehicles-filters-modal-key ::vehicles-filters-modal?)
(def transports-filters-key ::transports-filters)
(def transports-filters-open-key ::transports-filters-open?)
(def map-viewport-filter-field ::map-viewport)

(defn selected-cols [col-settings]
  (->> (columns/selected-profile (columns/from-localstorage col-settings))
       :cols
       (mapv (fn [{:keys [attr sticky?]}]
               {:attr attr
                :sticky? (boolean sticky?)}))))

(defn filter-signature [f]
  {::filters/field (::filters/field f)
   ::filters/operator (::filters/operator f)
   ::filters/value (::filters/value f)})

(defn summarize-list [xs]
  (let [items (vec xs)
        shown (take 3 items)]
    (str/join ", "
              (cond-> (vec shown)
                (> (count items) (count shown)) (conj "…")))))

(defn modified-view-lines [{:keys [current-snapshot baseline-snapshot field-spec-map]}]
  (let [baseline-cols (selected-cols (:col-settings baseline-snapshot))
        current-cols (selected-cols (:col-settings current-snapshot))
        baseline-attrs (mapv :attr baseline-cols)
        current-attrs (mapv :attr current-cols)
        baseline-attr-set (set baseline-attrs)
        current-attr-set (set current-attrs)
        added-cols (vec (remove baseline-attr-set current-attrs))
        removed-cols (vec (remove current-attr-set baseline-attrs))
        reordered? (and (= baseline-attr-set current-attr-set)
                        (not= baseline-attrs current-attrs))
        baseline-sticky (->> baseline-cols (filter :sticky?) (map :attr) set)
        current-sticky (->> current-cols (filter :sticky?) (map :attr) set)
        sticky-changed? (not= baseline-sticky current-sticky)
        baseline-filters (->> (filters/deserialize-filters (:filters baseline-snapshot))
                              (map filter-signature)
                              set)
        current-filters (->> (filters/deserialize-filters (:filters current-snapshot))
                             (map filter-signature)
                             set)
        added-filters (vec (set/difference current-filters baseline-filters))
        removed-filters (vec (set/difference baseline-filters current-filters))
        field-label (fn [field]
                      (or (get-in field-spec-map [field ::filters/label])
                          (pr-str field)))
        lines (cond-> []
                (seq added-cols)
                (conj (str (tr "Columns added") ": "
                           (summarize-list (map columns/label added-cols))))
                (seq removed-cols)
                (conj (str (tr "Columns removed") ": "
                           (summarize-list (map columns/label removed-cols))))
                reordered?
                (conj (tr "Column order changed"))
                sticky-changed?
                (conj (tr "Pinned columns changed"))
                (seq added-filters)
                (conj (str (tr "Filters added") ": "
                           (summarize-list (map (comp field-label ::filters/field) added-filters))))
                (seq removed-filters)
                (conj (str (tr "Filters removed") ": "
                           (summarize-list (map (comp field-label ::filters/field) removed-filters)))))]
    (if (seq lines)
      lines
      [(tr "Unsaved changes")])))

(defn vin-bulk-filter-item [{:keys [text all-vins]}]
  (let [vins (->> (re-seq #"\b[A-Z0-9]{3,17}\b" (or text ""))
                  (map str/upper-case)
                  distinct
                  vec)]
    (when (<= 2 (count vins))
      (let [non-matching (count (remove (set all-vins) vins))]
        [{::filters/id (random-uuid)
          ::filters/field :vehicle/vin
          ::filters/operator :matches
          ::filters/value {:any-of vins}
          ::filters/label (str (count vins) " " (tr "VINs")
                               (when (pos? non-matching)
                                 (str " · " (tr "%1 not found" non-matching))))}]))))

(defn vehicle-in-bounds? [[west south east north] vehicle]
  (let [lon (get-in vehicle [:transport/destination :location/longitude])
        lat (get-in vehicle [:transport/destination :location/latitude])]
    (and (number? lon)
         (number? lat)
         (<= south lat north)
         (if (<= west east)
           (<= west lon east)
           (or (<= west lon) (<= lon east))))))

(defn map-viewport-filter-item [rows bounds]
  (let [vins (->> rows
                  (filter #(vehicle-in-bounds? bounds %))
                  (keep :vehicle/vin)
                  (map str/upper-case)
                  distinct
                  vec)]
    {::filters/id (random-uuid)
     ::filters/field map-viewport-filter-field
     ::filters/operator :exact
     ::filters/value {:bounds bounds}
     ::filters/label (tr "Map Viewport")}))

(defn map-destination-id [vehicle]
  (let [{:address/keys [country postcode city street]} (:transport/destination vehicle)]
    (str (:country/code country) ", " postcode " " city ", " street)))

(defn vehicle-weight-kg [vehicle]
  (let [w (:vehicle/weight vehicle)]
    (cond
      (number? w) (double w)
      (string? w) (let [parsed (js/parseFloat w)]
                    (when-not (js/isNaN parsed)
                      parsed))
      :else nil)))

(defn vehicle-height-mm [vehicle]
  (let [h (:vehicle/height-mm vehicle)]
    (cond
      (number? h) (double h)
      (string? h) (let [parsed (js/parseFloat h)]
                    (when-not (js/isNaN parsed)
                      parsed))
      :else nil)))

(defn load-weight-label
  ([vehicle-ids vehicle-id->transport]
   (load-weight-label vehicle-ids vehicle-id->transport {:show-missing? false}))
  ([vehicle-ids vehicle-id->transport {:keys [show-missing?] :or {show-missing? false}}]
   (let [vehicles (vec (keep vehicle-id->transport vehicle-ids))
         weights (keep vehicle-weight-kg vehicles)
         kg (reduce + 0.0 weights)
         missing-count (- (count vehicles) (count weights))]
     (when (seq vehicles)
       (str (tr "Load weight")
            ": "
            (locale/format-number {:maximumFractionDigits 0} kg)
            " kg"
            (when (and show-missing? (pos? missing-count))
              (str " · " (tr "%1 vehicles missing weight" missing-count))))))))

(defn max-height-label
  ([vehicle-ids vehicle-id->transport]
   (max-height-label vehicle-ids vehicle-id->transport {:show-missing? false}))
  ([vehicle-ids vehicle-id->transport {:keys [show-missing?] :or {show-missing? false}}]
   (let [vehicles (vec (keep vehicle-id->transport vehicle-ids))
         heights (keep vehicle-height-mm vehicles)
         max-height (when (seq heights) (apply max heights))
         missing-count (- (count vehicles) (count heights))]
     (when (seq vehicles)
       (str (tr "Max height")
            ": "
            (if max-height
              (str (locale/format-number {:maximumFractionDigits 0} max-height) " mm")
              "—")
            (when (and show-missing? (pos? missing-count))
              (str " · " (tr "%1 vehicles missing height" missing-count))))))))

(defn upsert-map-viewport-filter [active-filters filter-item]
  (let [without-old (->> active-filters
                         (remove #(= map-viewport-filter-field (::filters/field %)))
                         vec)]
    (if filter-item
      (conj without-old filter-item)
      without-old)))

(def ^:private binding-priority :prioritiy/binding-customer-appointment)

(defn date-only-str [v]
  (when v
    (time-format/->str {:format :date} v)))

(defn binding-enum-value [row]
  (if (some #{binding-priority} (:fahrzeug/priorities row))
    (tr "Binding")
    (tr "Not Binding")))

(defn field-path [field]
  (if (vector? field) field [field]))

(defn datetime-like-keyword? [k]
  (let [n (name k)]
    (or (str/includes? n "datetime")
        (str/ends-with? n "-date"))))

(defn field-datetime? [field]
  (let [leaf (if (vector? field) (second field) field)]
    (and (keyword? leaf)
         (datetime-like-keyword? leaf))))

(defn field-flat-label [field]
  (if (= field :fahrzeug/priorities)
    (tr "Binding")
    (if (vector? field)
      (str (tr (first field)) " " (tr (second field)))
      (tr field))))

(declare delivery-availability-label)

(defn cardinal-direction-label [from to]
  (let [from-label (or (:address/postcode from) (:address/city from) (:address/name from))
        to-label (or (:address/postcode to) (:address/city to) (:address/name to))]
    (when (and from-label to-label)
      (str from-label " -> " to-label))))

(defn normalize-filter-value [v]
  (cond
    (nil? v) nil
    (string? v) (let [s (str/trim v)] (when (seq s) s))
    (number? v) v
    (boolean? v) v
    (keyword? v) (or (delivery-availability-label v) (tr v) (name v))
    (instance? js/Date v) v
    (map? v) (or (when (contains? v :country/code) (:country/code v))
                 (some-> (:db/ident v) normalize-filter-value)
                 (some-> (:location/summary v) normalize-filter-value)
                 (some-> (:priority/date v) normalize-filter-value)
                 (when (and (contains? v :cardinal-direction/from)
                            (contains? v :cardinal-direction/to))
                   (cardinal-direction-label (:cardinal-direction/from v) (:cardinal-direction/to v))))
    (coll? v) (let [vals (->> v
                              (map normalize-filter-value)
                              (remove nil?)
                              vec)]
                (when (seq vals) vals))
    :else (str v)))

(defn inferred-field-extract [field]
  (case field
    :fahrzeug/priorities
    binding-enum-value

    :fahrzeug/priority
    (fn [row]
      (normalize-filter-value (:fahrzeug/expected-customer-delivery-datetime row)))

    :fahrzeug/cardinal-direction
    (fn [{:transport/keys [origin destination]}]
      (normalize-filter-value {:cardinal-direction/from origin
                               :cardinal-direction/to destination}))

    (fn [row]
      (normalize-filter-value (get-in row (field-path field))))))

(def numeric-fields
  #{:vehicle/weight
    :vehicle/mileage
    :vehicle/volume
    :vehicle/width-mm
    :vehicle/length-mm
    :vehicle/height-mm
    :location/longitude
    :location/latitude})

(def set-like-fields
  #{:fahrzeug/labels
    :vehicle/engine-type
    :transport/mode
    [:transport/destination :location/night-delivery]
    [:transport/destination :location/saturday-delivery]})

(defn infer-field-type [field sample-values]
  (cond
    (= field :fahrzeug/priorities) :text
    (contains? numeric-fields field) :number
    (field-datetime? field) :date
    (contains? set-like-fields field) :set
    (some coll? sample-values) :set
    (and (seq sample-values) (every? boolean? sample-values)) :boolean
    (and (seq sample-values) (every? number? sample-values)) :number
    (and (seq sample-values)
         (every? #(some? (filters/value->comparable :date %)) sample-values))
    :date
    :else :text))

(defn operators-for-type [field-type]
  (case field-type
    :boolean #{:boolean}
    :set #{:exact}
    :number #{:exact :range}
    :date #{:exact :range}
    #{:exact :contains :matches :not-contains :not-matches}))

(defn values-for-choices [rows extract]
  (->> rows
       (map extract)
       (mapcat (fn [v]
                 (if (coll? v) v [v])))
       (remove nil?)
       (map str)
       distinct
       sort
       vec))

(defn transport-display-name [transport-id transport draft-index]
  (if draft-index
    (or (:draft/name transport)
        (str (tr "Draft transport") " " draft-index))
    (or (:load/id transport)
        (tr "Transport"))))

(defn transport-waypoints-text [waypoints]
  (when (seq waypoints)
    (str/join " -> " (map panels/waypoint-label waypoints))))

(defn transport-filter-rows [loads draft-transports ordered-draft-ids transport-summaries]
  (let [persisted (->> loads
                       (sort-by (juxt (comp boolean :ductile/created-at)
                                      :ductile/created-at)
                                #(compare %2 %1))
                       (map (fn [load]
                              {:transport-id (selectors/load-key load)
                               :transport load
                               :draft-index nil})))
        drafts (map-indexed (fn [idx transport-id]
                              {:transport-id transport-id
                               :transport (get draft-transports transport-id)
                               :draft-index (inc idx)})
                            ordered-draft-ids)]
    (->> (concat drafts persisted)
         (mapv (fn [{:keys [transport-id transport draft-index]}]
                 (let [{:keys [waypoints vehicle-count carrier-label]} (get transport-summaries transport-id)
                       transporter (:transport/transporter transport)
                       carrier (or (:transport/carrier transport)
                                   (:transport/carrier transporter))
                       truck-or-carrier-code (or (:truck/number transporter)
                                                 (:carrier/code carrier))
                       license-plate (or (:transport/license-plate transport)
                                         (:truck/license-plate transporter))
                       draft? (some? draft-index)]
                   {:transport-id transport-id
                    :transport/display-name (transport-display-name transport-id transport draft-index)
                    :transport/status-label (some-> (:transport/status transport) tr)
                    :transport/carrier-truck (str/join " "
                                                       (remove str/blank?
                                                               [(or carrier-label "")
                                                                (or truck-or-carrier-code "")]))
                    :transport/license-plate (or license-plate "")
                    :transport/waypoints-text (or (transport-waypoints-text waypoints) "")
                    :transport/vehicle-count (or vehicle-count 0)
                    :transport/draft? draft?})))
         vec)))

(defn transport-field-specs [rows]
  [{::filters/field :transport/display-name
    ::filters/label (tr "Load")
    ::filters/type :text
    ::filters/operators #{:contains}}
   {::filters/field :transport/status-label
    ::filters/label (tr "Status")
    ::filters/type :text
    ::filters/operators #{:exact}
    ::filters/choices-fn (fn [_]
                           (->> rows
                                (keep :transport/status-label)
                                distinct
                                sort
                                vec))}
   {::filters/field :transport/carrier-truck
    ::filters/label (tr "Carrier / Truck")
    ::filters/type :text
    ::filters/operators #{:contains}}
   {::filters/field :transport/license-plate
    ::filters/label (tr "License Plate")
    ::filters/type :text
    ::filters/operators #{:contains}}
   {::filters/field :transport/waypoints-text
    ::filters/label (tr "Waypoints")
    ::filters/type :text
    ::filters/operators #{:contains}}
   {::filters/field :transport/vehicle-count
    ::filters/label (tr "Vehicle count")
    ::filters/type :number
    ::filters/operators #{:range}}
   {::filters/field :transport/draft?
    ::filters/label (tr "Draft")
    ::filters/type :boolean
    ::filters/operators #{:boolean}}])

(defn vehicles-field-specs [rows available-cols current-map-bounds-fn]
  (let [available-vins (set (map (comp str/upper-case :vehicle/vin) rows))
        all-fields (->> available-cols
                        (mapcat columns/expand-col)
                        (map :attr)
                        distinct
                        vec)
        field-specs
        (mapv (fn [field]
                (let [extract (inferred-field-extract field)
                      sample-values (->> rows
                                         (map extract)
                                         (remove nil?)
                                         (take 30)
                                         vec)
                      field-type (infer-field-type field sample-values)
                      spec {::filters/field field
                            ::filters/label (field-flat-label field)
                            ::filters/type field-type
                            ::filters/extract extract
                            ::filters/operators (operators-for-type field-type)}
                      spec (if (= field :fahrzeug/priorities)
                             (assoc spec
                                    ::filters/type :text
                                    ::filters/operators #{:exact}
                                    ::filters/choices-fn (fn [_]
                                                           [(tr "Binding")
                                                            (tr "Not Binding")]))
                             spec)
                      spec (if (= field :vehicle/vin)
                             (assoc spec
                                    ::filters/text->filter (fn [filter-item]
                                                             (or (vin-bulk-filter-item {:text (::filters/value filter-item)
                                                                                        :all-vins available-vins})
                                                                 filter-item)))
                             spec)
                      spec (if (or (= field-type :set)
                                   (contains? set-like-fields field))
                             (assoc spec
                                    ::filters/choices-fn (fn [all]
                                                           (values-for-choices all extract)))
                             spec)]
                  spec))
              all-fields)]
    (conj field-specs
          {::filters/field map-viewport-filter-field
           ::filters/label (tr "Map Viewport")
           ::filters/type :boolean
           ::filters/operators #{:boolean}
           ::filters/fixed-filter? true
           ::filters/text->filter (fn [_]
                                    (if-let [bounds (current-map-bounds-fn)]
                                      [(map-viewport-filter-item rows bounds)]
                                      []))
           ;; Dynamic like LBv1 "viewport filter": evaluate against the live map bounds.
           ::filters/predicate (fn [_]
                                 (fn [vehicle]
                                   (if-let [bounds (current-map-bounds-fn)]
                                     (vehicle-in-bounds? bounds vehicle)
                                     true)))})))

(defn popup? []
  (= "1" (.get (js/URLSearchParams. (.-search js/location)) "popup")))

(defn popup-panel-id []
  (some-> (.get (js/URLSearchParams. (.-search js/location)) "panel") keyword))

(defn session-id []
  (.get (js/URLSearchParams. (.-search js/location)) "session"))

(defn debug-enabled? []
  (= "1" (.get (js/URLSearchParams. (.-search js/location)) "lbdebug")))

(defn shared-state-key [sid]
  (str "ductile.ui.load-builder-v2.shared." sid))

(defn heartbeat-key [sid]
  (str "ductile.ui.load-builder-v2.heartbeat." sid))

(defn ops-key [sid]
  (str "ductile.ui.load-builder-v2.ops." sid))

(defn trace-key [sid]
  (str "ductile.ui.load-builder-v2.trace." sid))

(defn now-ms []
  (.now js/Date))

(defn log-lbv2! [label data]
  nil)

(defn push-trace! [sid entry]
  (when (and sid (debug-enabled?))
    (let [k (trace-key sid)
          raw (js/localStorage.getItem k)
          entries (if raw
                    (or (reader/read-string raw) [])
                    [])
          entry' (assoc entry :t (now-ms))
          entries' (conj (vec entries) entry')
          trimmed (if (> (count entries') 400)
                    (vec (take-last 400 entries'))
                    entries')]
      (js/localStorage.setItem k (pr-str trimmed))
      (js/console.debug "[lbv2-trace]" (clj->js entry')))))

(defn normalize-message-type [type]
  (cond
    (keyword? type)
    (case (name type)
      "focus" :route/focus
      "hide" :route/hide
      "create" :draft/create
      "apply" :op/apply
      "ack" :op/ack
      "snapshot" :server/snapshot
      (if-let [ns (namespace type)]
        (keyword ns (name type))
        (keyword (name type))))

    (string? type)
    (case type
      "focus" :route/focus
      "hide" :route/hide
      "create" :draft/create
      "route/focus" :route/focus
      "route/hide" :route/hide
      "draft/create" :draft/create
      "op/apply" :op/apply
      "op/ack" :op/ack
      "server/snapshot" :server/snapshot
      nil)

    :else nil))

(defn transport-id->wire [transport-id]
  (cond
    (keyword? transport-id) (name transport-id)
    :else (str transport-id)))

(defn vehicle-id->wire [vehicle-id]
  (str vehicle-id))

(defn resolve-vehicle-id [raw-id]
  (cond
    (uuid? raw-id) raw-id
    (string? raw-id) (try
                       (uuid raw-id)
                       (catch :default _ raw-id))
    :else raw-id))

(defn normalize-op [op]
  (cond
    (keyword? op) op
    (string? op) (keyword op)
    :else op))

(defn make-message [session-id type payload]
  {:source "load-builder-v2"
   :session-id session-id
   :type (case (normalize-message-type type)
           :route/focus "route/focus"
           :route/hide "route/hide"
           :draft/create "draft/create"
           :op/apply "op/apply"
           :op/ack "op/ack"
           :server/snapshot "server/snapshot"
           nil)
   :payload payload
   :id (str (random-uuid))
   :ts (now-ms)})

(defn make-op-message [session-id op-id payload]
  (assoc (make-message session-id :op/apply payload)
         :id op-id))

(defn make-ack-message [session-id op-id ok? error]
  (assoc (make-message session-id :op/ack {:ok? ok?
                                           :error error})
         :id op-id))

(defn post-message! [target message]
  (try
    (when target
      (.postMessage target (clj->js message) "*")
      true)
    (catch :default _ false)))

(defn post-op! [target session-id op-id payload]
  (post-message! target (make-op-message session-id op-id payload)))

(defn ack-op! [target session-id op-id ok? error]
  (post-message! target (make-ack-message session-id op-id ok? error)))

(defn broadcast-message! [session-id targets type payload]
  (let [message (make-message session-id type payload)]
    (boolean (some true? (map #(post-message! % message) targets)))))

(defn valid-message? [session-id message]
  (let [source (:source message)
        type (normalize-message-type (:type message))]
    (and (map? message)
         (= "load-builder-v2" source)
         (= session-id (:session-id message))
         (contains? #{:route/focus :route/hide :draft/create :op/apply :op/ack :server/snapshot} type)
         (string? (:id message)))))

(defn popup-url [panel-id sid]
  (let [params (js/URLSearchParams. (.-search js/location))
        debug? (= "1" (.get params "lbdebug"))]
    (str js/location.pathname
         "/popup?popup=1&panel=" (name panel-id)
         "&session=" sid
         (when debug?
           "&lbdebug=1"))))

(defn event->cell-col-path [event]
  (when-let [raw (some-> event
                         .-target
                         (.closest "td[data-col-path]")
                         (.getAttribute "data-col-path"))]
    (try
      (reader/read-string raw)
      (catch :default _
        nil))))

(defn value->filter-input [field-type value]
  (case field-type
    :boolean (boolean value)
    :number (let [n (if (number? value) value (js/parseFloat (str value)))]
              (when-not (js/isNaN n) n))
    :date (cond
            (instance? js/Date value) (subs (.toISOString ^js value) 0 10)
            (some? value) (or (date-only-str value) (str value))
            :else nil)
    :set (cond
           (string? value) value
           (coll? value) (or (first (seq value)) nil)
           :else value)
    (when (some? value) (str value))))

(defn delivery-availability-label [v]
  (let [v' (cond
             (map? v) (:db/ident v)
             (keyword? v) v
             (string? v) (keyword "delivery.availability" v)
             :else nil)]
    (when v'
      (tr v'))))

(defn cell->menu-filter-action [field-spec-map vehicle event]
  (when-let [field (event->cell-col-path event)]
    (when-let [field-spec (get field-spec-map field)]
      (let [field-label (::filters/label field-spec)
            field-type (::filters/type field-spec)
            extracted (filters/extract-value vehicle field-spec)
            extracted-coll? (and (coll? extracted) (not (string? extracted)))
            extracted-seq (when extracted-coll? (vec (remove nil? extracted)))
            complex? (or (and extracted-coll? (not= 1 (count extracted-seq)))
                         (map? extracted))
            extracted' (if (and (coll? extracted-seq) (= 1 (count extracted-seq)))
                         (first extracted-seq)
                         extracted)
            filter-value (value->filter-input field-type extracted')
            has-value? (and (some? filter-value)
                            (not (and (string? filter-value)
                                      (str/blank? filter-value))))
            filter-item {::filters/id (random-uuid)
                         ::filters/field field
                         ::filters/operator (cond
                                              (= :boolean field-type) :boolean
                                              (and complex? (= field-type :set)) :contains
                                              complex? :contains
                                              :else :exact)
                         ::filters/value filter-value}
            filter-item (assoc filter-item
                               ::filters/label (filters/label-for-filter field-spec filter-item))]
        (when has-value?
          {:mode (if complex? :prefill-modal :apply)
           :filter-item filter-item
           :label (if complex?
                    (tr "Add filter for %1…" field-label)
                    (tr "Filter by %1: %2" field-label (str filter-value)))})))))

(defn date-transform-column [field]
  (fn [row]
    (some-> (get-in row (field-path field))
            date-only-str)))

(defn binding-label [row]
  (when (some #{binding-priority} (:fahrzeug/priorities row))
    (tr "Binding")))

(defn date-transform-columns [available-cols]
  (->> available-cols
       (mapcat columns/expand-col)
       (map :attr)
       (filter field-datetime?)
       distinct
       (map (fn [field] [(if (vector? field) field [field])
                         (date-transform-column field)]))
       (into {})))

(defn vehicles-table-opts [{:keys [col-settings available-cols row-action row-double-action row-on-context-menu
                                   header-on-context-menu
                                   selected-rows
                                   drag-select? on-drag-select on-drag-select-preview on-drag-select-commit
                                   virtualize-rows? virtual-scroll-container-selector
                                   header-indicator-fn
                                   !sorting]
                            :or {virtualize-rows? false}}]
  (merge
   {:row-key :ductile/id
    :row-action row-action
    :row-action-with-event? true
    :row-double-action row-double-action
    :row-double-action-with-event? true
    :row-on-context-menu row-on-context-menu
    :header-on-context-menu header-on-context-menu
    :!sorting !sorting
    :selected-rows selected-rows
    :drag-select? drag-select?
    :on-drag-select-preview on-drag-select-preview
    :on-drag-select-commit on-drag-select-commit
    :on-drag-select on-drag-select
    :header-indicator-fn header-indicator-fn
    :virtualize-rows? virtualize-rows?
    :virtual-row-height 30
    :virtual-overscan 8
    :virtual-scroll-container-selector virtual-scroll-container-selector
    :resizable-cols? true
    :schema lb1/transports-table-schema
    :translated-keys {:fahrzeug/cardinal-direction (tr "Direction")
                      :fahrzeug/parking-spot (tr "Parking spot")
                      :fahrzeug/priority (tr "Priority")
                      :fahrzeug/priorities (tr "Binding")
                      :transport/origin (tr "Origin")
                      :compound/name (tr "Compound")
                      :location/latitude (tr "Latitude")
                      :location/longitude (tr "Longitude")
                      :address/country (tr "Country")
                      :address/city (tr "City")
                      :address/postcode (tr "Postcode")
                      :address/street (tr "Street")
                      :address/name (tr "Address name")
                      :location/delivery-hours (tr "Delivery hours")
                      :transport/destination (tr "Destination")
                      :load/id (tr "Load ID")
                      :make/name (tr "Make")
                      :vehicle/weight (tr "Weight")
                      :vehicle/model (tr "Model")
                      :contract/name (tr "Contract")
                      :fahrzeug/labels (tr "Labels")}
    :column-layout {:fahrzeug/cardinal-direction {:min-width 95}
                    :fahrzeug/priority {:min-width 85}
                    :fahrzeug/priorities {:min-width 85}
                    :fahrzeug/labels {:min-width 90}
                    [:transport/origin :address/postcode] {:min-width 114}
                    [:transport/origin :address/city] {:min-width 140}
                    [:transport/destination :address/postcode] {:min-width 114}
                    [:transport/destination :address/city] {:min-width 255}
                    :vehicle/vin {:min-width 150}
                    :make/name {:min-width 100}
                    :load/id {:min-width 100}
                    :fahrzeug/parking-spot {:min-width 116}}
    :transform-columns
    (merge
     {[:transport/origin :address/postcode]
      (fn [{orig :transport/origin}]
        (when (and orig (:address/postcode orig))
          {:location/summary (:address/postcode orig)
           :location/details orig
           :viewer/css-class "tabular-nums text-right"}))
      [:transport/destination :address/postcode]
      (fn [{dest :transport/destination}]
        (when (and dest (:address/postcode dest))
          {:location/summary (:address/postcode dest)
           :location/details dest
           :viewer/css-class "tabular-nums text-right"}))
      [:transport/destination :location/night-delivery]
      (fn [{dest :transport/destination}]
        (delivery-availability-label (:location/night-delivery dest)))
      [:transport/destination :location/saturday-delivery]
      (fn [{dest :transport/destination}]
        (delivery-availability-label (:location/saturday-delivery dest)))
      [:transport/destination :location/delivery-hours]
      (fn [{dest :transport/destination}]
        (ui-util/translate-opening-hours-day-components (:location/delivery-hours dest)))
      [:vehicle/vin] (fn [{:vehicle/keys [vin]}] [:span.font-mono vin])
      [:fahrzeug/priorities] binding-label}
     (date-transform-columns available-cols))
    :computed-columns {:fahrzeug/cardinal-direction
                       (fn [{:transport/keys [origin destination]}]
                         (when (and origin destination)
                           {:cardinal-direction/from origin :cardinal-direction/to destination}))
                       :fahrzeug/priority
                       (fn [{date :fahrzeug/expected-customer-delivery-datetime}]
                         (when date {:priority/date date}))}}
   (columns/->table-opts {:col-settings col-settings
                          :available-cols available-cols
                          :default-selected-cols default-vehicles-column-order})))

(defn- existing-transport-id? [transport-id]
  (uuid? transport-id))

(defn- sanitize-submit-load [load]
  (cond-> (dissoc (or load {})
                  :draft?
                  :draft/name
                  :transport/kind
                  :transport/finalized-at)
    (keyword? (:ductile/id load))
    (dissoc :ductile/id)))

(defn show-success-toast-tx [transport {:keys [updated?]}]
  [[:op/assoc :toast {:title (str "✅ " (if updated?
                                         (tr "Transport changes saved.")
                                         (tr "Transport successfully created")))
                      :description (if updated?
                                     (tr "Your changes to %1 are now available." (:load/id transport))
                                     (tr "Click here to show the modified transport."))
                      :action {:as :a
                               :href (transport/transport-link transport)
                               :variant :success
                               :label (str "🚛 " (tr "Show transport"))}
                      :variant :success}]])

(defn show-remove-from-load-toast-tx [vehicle-count]
  [[:op/assoc :toast {:title (str "✅ " (if (= 1 vehicle-count)
                                         (tr "Vehicle removed from load.")
                                         (tr "%1 vehicles removed from load." vehicle-count)))
                      :variant :success}]])

(defn submit-transport! [{:keys [transport-id draft? load transport->vehicle-ids vehicle-id->transport
                                 transport->waypoint-settings on-success]}]
  (let [vehicle-ids (get transport->vehicle-ids transport-id [])
        selected-fahrzeuge (vec (keep vehicle-id->transport vehicle-ids))
        sanitized-load (sanitize-submit-load load)
        persisted-waypoint-order (selectors/transport-persisted-waypoint-order-from-transport load)
        ordered-waypoints (selectors/transport-waypoints transport-id
                                                         transport->vehicle-ids
                                                         vehicle-id->transport
                                                         transport->waypoint-settings
                                                         persisted-waypoint-order)
        ordered-destination-ids (->> ordered-waypoints
                                     (keep :ductile/id)
                                     vec)
        destination-ids (if (seq ordered-destination-ids)
                          ordered-destination-ids
                          (->> selected-fahrzeuge
                               (keep (comp :ductile/id :transport/destination))
                               distinct
                               vec))
        payload (merge sanitized-load
                       (deframe/current-route-params)
                       {:destination/ductile-ids destination-ids
                        :vehicle-search-params {:search-params {:ductile/id (set vehicle-ids)}}})
        updated? (and (not draft?)
                      (existing-transport-id? (:ductile/id sanitized-load)))]
    (-> (http/$lambda 'ductile.transport/handle-assign-exit-transport payload)
        (.then (fn [result]
                 (if (:error result)
                   (deframe/tx! [[:op/assoc-in [:current-route/transient :fetch :error] result]])
                   (do
                     (deframe/tx! [[:op/dissoc-in [:current-route/transient :fetch :error]]])
                     (deframe/tx! (show-success-toast-tx result {:updated? updated?}))
                     (deframe/route-refetch!)
                     (on-success {:transport-id transport-id :draft? draft? :result result}))))))))

(defn remove-vehicles-from-load! [{:keys [transport-id vehicle-ids draft-ids
                                          popup-mode?
                                          dispatch!
                                          compute-focused-route! clear-focused-route!
                                          state]}]
  (let [draft? (contains? draft-ids transport-id)
        vehicle-ids (vec (distinct vehicle-ids))
        update-focused-route! (fn []
                                (if (= transport-id (:focused-transport-id @state))
                                  (let [remaining-vehicle-ids (get-in @state [:transport->vehicle-ids transport-id])]
                                    (if (seq remaining-vehicle-ids)
                                      (compute-focused-route! transport-id
                                                              (:transport->vehicle-ids @state)
                                                              (:transport->waypoint-settings @state))
                                      (clear-focused-route!)))
                                  nil))]
    (dispatch! {:event :transport/remove-from-load
                :transport-id transport-id
                :vehicle-ids vehicle-ids})
    (update-focused-route!)
    (if (or draft? popup-mode?)
      (.resolve js/Promise nil)
      (-> (reduce
           (fn [promise vehicle-id]
             (.then promise
                    (fn [_]
                      (-> (http/$lambda 'ductile.transport/remove-vehicle
                                        {:ductile/id vehicle-id
                                         :phase :exit})
                          (.then (fn [result]
                                   (if (:error result)
                                     (js/Promise.reject result)
                                     result)))))))
           (.resolve js/Promise nil)
           vehicle-ids)
          (.then (fn [_]
                   (deframe/tx! [[:op/dissoc-in [:current-route/transient :fetch :error]]])
                   (deframe/tx! (show-remove-from-load-toast-tx (count vehicle-ids)))
                   (deframe/route-refetch!)))
          (.catch (fn [error]
                    (deframe/tx! [[:op/assoc-in [:current-route/transient :fetch :error]
                                   (if (map? error)
                                     error
                                     {:error :transport-error/remove-vehicle-failed
                                      :error-message (or (ex-message error)
                                                         (str error))})]])
                    (deframe/route-refetch!)))))))

(defn route-for-vehicle-ids!
  ([vehicle-ids vehicle-id->transport !focused-route]
   (route-for-vehicle-ids! vehicle-ids vehicle-id->transport !focused-route (fn [_] nil)))
  ([vehicle-ids vehicle-id->transport !focused-route on-complete]
   (let [transports (vec (keep vehicle-id->transport vehicle-ids))
         origin (some :transport/origin transports)
         origin-coord (when origin
                        [(:location/longitude origin)
                         (:location/latitude origin)])
         destination-coords (cond->> (->> transports
                                          (map :transport/destination)
                                          (map (fn [{:location/keys [longitude latitude]}]
                                                 [longitude latitude]))
                                          (filter (fn [[lon lat]] (and (number? lon) (number? lat))))
                                          distinct
                                          vec)
                              (and (number? (first origin-coord))
                                   (number? (second origin-coord)))
                              (lb1/sort-by-distance-to-origin origin-coord))]
     (if (and origin (seq destination-coords)
              (number? (:location/longitude origin))
              (number? (:location/latitude origin)))
       (-> (transport/mapbox-route [(:location/longitude origin) (:location/latitude origin)]
                                   destination-coords
                                   true)
           (.then (fn [^js res]
                    (if (= "Ok" (.-code res))
                      (let [route (j/get-in res [:trips 0])
                            waypoints (j/get res :waypoints)
                            route-data (transport/js->route-data route waypoints)]
                        (reset! !focused-route route-data)
                        (on-complete {:status :ok
                                      :route route-data}))
                      (do
                        (reset! !focused-route nil)
                        (on-complete {:status :error
                                      :route nil})))))
           (.catch (fn [_]
                     (reset! !focused-route nil)
                     (on-complete {:status :error
                                   :route nil}))))
       (do
         (reset! !focused-route nil)
         (on-complete {:status :empty
                       :route nil}))))))

(defn waypoint->coord [{:location/keys [longitude latitude]}]
  (when (and (number? longitude) (number? latitude))
    [longitude latitude]))

(defn grouped-waypoints-by-coord [waypoints]
  (reduce (fn [{:keys [seen groups]} waypoint]
            (if-let [coord (waypoint->coord waypoint)]
              (if (contains? seen coord)
                {:seen seen
                 :groups (mapv (fn [group]
                                 (if (= coord (:coord group))
                                   (update group :waypoints conj waypoint)
                                   group))
                               groups)}
                {:seen (conj seen coord)
                 :groups (conj groups {:coord coord
                                       :waypoints [waypoint]})})
              {:seen seen
               :groups groups}))
          {:seen #{}
           :groups []}
          waypoints))

(defn optimize-waypoints!
  [{:keys [origin waypoints]}]
  (if (and origin
           (number? (:location/longitude origin))
           (number? (:location/latitude origin))
           (<= 2 (count waypoints)))
    (let [origin-coord [(:location/longitude origin) (:location/latitude origin)]
          grouped-waypoints (:groups (grouped-waypoints-by-coord waypoints))
          destination-coords (->> grouped-waypoints
                                  (map :coord)
                                  (lb1/sort-by-distance-to-origin origin-coord)
                                  vec)]
      (-> (transport/mapbox-route origin-coord
                                  destination-coords
                                  true)
          (.then (fn [^js res]
                   (if (= "Ok" (.-code res))
                     (let [js-waypoints (j/get res :waypoints)
                           reordering (into {}
                                            (rest (.map js-waypoints
                                                        (fn [w index]
                                                          ;; `dec` to exclude origin from index calculations.
                                                          [(dec index) (dec (j/get w :waypoint_index))]))))
                           reordered-groups (mapv (fn [i]
                                                    (nth grouped-waypoints (get reordering i i)))
                                                  (range (count grouped-waypoints)))]
                       (vec (mapcat :waypoints reordered-groups)))
                     (throw (js/Error. (str "Mapbox optimization failed with code " (.-code res)))))))))
    (js/Promise.resolve (vec waypoints))))

(defn- normalize-route-stop-indices! [route-data]
  (when-let [features (j/get-in route-data [:stops :features])]
    (.forEach features
              (fn [feature idx]
                (j/assoc-in! feature [:properties :index] idx))))
  route-data)

(defn- valid-origin-coords? [origin]
  (and (number? (:location/longitude origin))
       (number? (:location/latitude origin))))

(defn- pick-route-origin [vehicle-ids vehicle-id->transport]
  (let [origins (->> vehicle-ids
                     (keep vehicle-id->transport)
                     (keep :transport/origin)
                     (remove nil?)
                     vec)
        with-city (filter #(seq (:address/city %)) origins)
        preferred-by-city
        (when (seq with-city)
          (->> with-city
               (group-by :address/city)
               (sort-by (fn [[city xs]]
                          [(- (count xs))
                           city]))
               ffirst))
        preferred-origin
        (when preferred-by-city
          (some #(when (= preferred-by-city (:address/city %)) %) origins))
        origin (or (some #(when (and (= preferred-by-city (:address/city %))
                                     (valid-origin-coords? %))
                            %)
                         origins)
                   (some #(when (valid-origin-coords? %) %) origins)
                   preferred-origin
                   (first origins))]
    origin))

(defn route-for-transport!
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings transport !focused-route]
   (route-for-transport! transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings transport !focused-route (fn [_] nil)))
  ([transport-id transport->vehicle-ids vehicle-id->transport transport->waypoint-settings transport !focused-route on-complete]
   (let [vehicle-ids (get transport->vehicle-ids transport-id [])
         transports (vec (keep vehicle-id->transport vehicle-ids))
         origin (or (some :transport/origin transports)
                    (pick-route-origin vehicle-ids vehicle-id->transport))
         origin-coord (when origin
                        [(:location/longitude origin)
                         (:location/latitude origin)])
         persisted-waypoint-order (selectors/transport-persisted-waypoint-order-from-transport transport)
         optimize? (selectors/transport-optimized-route? transport-id
                                                         transport->vehicle-ids
                                                         vehicle-id->transport
                                                         transport->waypoint-settings
                                                         persisted-waypoint-order)
         waypoints (selectors/transport-waypoints transport-id
                                                  transport->vehicle-ids
                                                  vehicle-id->transport
                                                  transport->waypoint-settings
                                                  persisted-waypoint-order)
         destination-coords (->> waypoints
                                 (keep waypoint->coord)
                                 distinct
                                 vec)
         optimized-destination-coords (cond->> (lb1/->waypoint-coords transports)
                                        (and (number? (first origin-coord))
                                             (number? (second origin-coord)))
                                        (lb1/sort-by-distance-to-origin origin-coord))
         destination-coords (if optimize?
                              optimized-destination-coords
                              destination-coords)]
     (if (and origin (seq destination-coords)
              (number? (:location/longitude origin))
              (number? (:location/latitude origin)))
       (-> (transport/mapbox-route [(:location/longitude origin) (:location/latitude origin)]
                                   destination-coords
                                   optimize?)
           (.then (fn [^js res]
                    (if (= "Ok" (.-code res))
                      (let [[route waypoints']
                            (if optimize?
                              [(j/get-in res [:trips 0])
                               (or (j/get res :waypoints) #js [])]
                              [(j/get-in res [:routes 0])
                               (or (j/get-in res [:routes 0 :waypoints]) #js [])])
                            route-data (cond-> (transport/js->route-data route waypoints')
                                         (not optimize?)
                                         normalize-route-stop-indices!)]
                        (reset! !focused-route route-data)
                        (on-complete {:status :ok
                                      :route route-data}))
                      (do
                        (reset! !focused-route nil)
                        (on-complete {:status :error
                                      :route nil})))))
           (.catch (fn [_]
                     (reset! !focused-route nil)
                     (on-complete {:status :error
                                   :route nil}))))
       (do
         (reset! !focused-route nil)
         (on-complete {:status :empty
                       :route nil}))))))

(defn load-builder-v2 [{:keys [fahrzeuge loads available-actions] :as fetch-result}]
  (let [popup-mode? (popup?)
        !sid (hooks/use-state (or (session-id) (str (random-uuid))))
        !window-id (hooks/use-state (str (random-uuid)))
        sid @!sid
        window-id @!window-id
        raw-fahrzeuge fahrzeuge
        raw-loads loads
        raw-available-actions available-actions
        raw-diff-basis-t (:ductile.lambda/diff-basis-t fetch-result)
        !server-data (hooks/use-state {:fahrzeuge raw-fahrzeuge
                                       :loads raw-loads
                                       :available-actions raw-available-actions
                                       :ductile.lambda/diff-basis-t raw-diff-basis-t})
        server-data (if popup-mode?
                      @!server-data
                      {:fahrzeuge raw-fahrzeuge
                       :loads raw-loads
                       :available-actions raw-available-actions
                       :ductile.lambda/diff-basis-t raw-diff-basis-t})
        fahrzeuge (:fahrzeuge server-data)
        loads (:loads server-data)
        available-actions (:available-actions server-data)
        shared-bootstrap (when popup-mode?
                           (when-let [raw (js/localStorage.getItem (shared-state-key sid))]
                             (try
                               (reader/read-string raw)
                               (catch :default _
                                 nil))))
        {:keys [!state dispatch!]}
        (lbhooks/use-load-builder-v2-state {:loads loads
                                            :fahrzeuge fahrzeuge
                                            :shared shared-bootstrap
                                            :layout (or @(deframe/!localstorage-get :ductile.ui.load-builder-v2/layout)
                                                        (paneling/default-layout panel-ids))})
        ui-state @!state
        !route-request-token (hooks/use-ref 0)
        !seen-message-ids (hooks/use-ref #{})
        !seen-message-order (hooks/use-ref [])
        !applied-op-ids (hooks/use-ref #{})
        !applied-op-order (hooks/use-ref [])
        !last-shared-focused-id (hooks/use-ref nil)
        !pending-created-draft-focus (hooks/use-ref nil)
        !views-hydrated? (hooks/use-ref false)
        !last-update-ms (hooks/use-state nil)
        !map-viewport-rev (hooks/use-state 0)
        !remove-from-load-confirmation (hooks/use-state nil)
        auto-refresh-seconds (some-> (:ductile.lambda/refetch-ms fetch-result) (/ 1000) int)
        col-settings @(deframe/!localstorage-get vehicles-col-storage-key)
        persisted-views-state @(deframe/!localstorage-get views-storage-key)
        active-filters (filters/deserialize-filters @(deframe/!route-get vehicles-filters-key))
        serialized-active-filters (filters/serialize-filters active-filters)
        draft-filters (filters/deserialize-filters @(deframe/!route-get vehicles-filters-draft-key))
        filters-modal-open? (boolean @(deframe/!route-get vehicles-filters-modal-key))
        active-transport-filters (filters/deserialize-filters @(deframe/!route-get transports-filters-key))
        transport-filters-open? (boolean @(deframe/!route-get transports-filters-open-key))
        map-viewport-rev @!map-viewport-rev
        {:keys [storage-key default?]} (lb1/show-fahrzeuge-on-transports-setting)
        show-fahrzeuge-on-transports? @(deframe/!localstorage-get storage-key default?)
        vehicle-id->transport (selectors/vehicles-by-id fahrzeuge)
        draft-transports (:draft-transports ui-state)
        draft-order (:draft-order ui-state)
        transport->vehicle-ids (:transport->vehicle-ids ui-state)
        transport->waypoint-settings (:transport->waypoint-settings ui-state)
        staging-ids (:staging-ids ui-state)
        selected-vehicle-ids (:selected-vehicle-ids ui-state)
        focused-transport-id (:focused-transport-id ui-state)
        focused-selection-vehicle-ids (:focused-selection-vehicle-ids ui-state)
        focused-route (:focused-route ui-state)
        focused-route-fit-key (:focused-route-fit-key ui-state)
        route-loading? (:route-loading? ui-state)
        route-loading-transport-id (:route-loading-transport-id ui-state)
        pending-ops (:pending-ops ui-state)
        transport-update-tokens (:transport-update-tokens ui-state)
        layout (:layout ui-state)
        popouts (:popouts ui-state)
        disconnected? (:disconnected? ui-state)
        editing-transport-id (:editing-transport-id ui-state)
        show-edit-transport-modal? (boolean @(deframe/!route-get ::transport/edit-transport))
        show-cols-modal? (:show-cols-modal? ui-state)
        views (:views ui-state)
        selected-view-id (:selected-view-id ui-state)
        view-baseline-snapshot (:view-baseline-snapshot ui-state)
        menu (:menu ui-state)
        staging-id-set (hooks/use-memo #(set staging-ids) [staging-ids])
        draft-ids (hooks/use-memo #(set (keys draft-transports)) [draft-transports])
        ordered-draft-ids (hooks/use-memo #(selectors/ordered-draft-ids draft-ids draft-order) [draft-ids draft-order])
        draft-index-by-id (hooks/use-memo #(selectors/draft-index-by-id ordered-draft-ids) [ordered-draft-ids])
        all-transport-ids (hooks/use-memo #(vec (concat (map selectors/load-key loads) (keys draft-transports)))
                                          [loads draft-transports])
        transport-id->load (hooks/use-memo #(into {} (map (juxt selectors/load-key identity) loads)) [loads])
        transport-id->entity (hooks/use-memo #(merge transport-id->load draft-transports)
                                             [transport-id->load draft-transports])
        assigned-ids (hooks/use-memo #(selectors/assignments->assigned-ids transport->vehicle-ids) [transport->vehicle-ids])
        transport-id->persisted-waypoint-order (hooks/use-memo
                                                #(into {}
                                                       (map (fn [[transport-id transport]]
                                                              [transport-id
                                                               (selectors/transport-persisted-waypoint-order-from-transport transport)]))
                                                       transport-id->entity)
                                                [transport-id->entity])
        transport-summaries (hooks/use-memo #(selectors/transport-summaries all-transport-ids
                                                                            transport->vehicle-ids
                                                                            vehicle-id->transport
                                                                            transport->waypoint-settings
                                                                            transport-id->persisted-waypoint-order)
                                            [all-transport-ids
                                             transport->vehicle-ids
                                             vehicle-id->transport
                                             transport->waypoint-settings
                                             transport-id->persisted-waypoint-order])
        transport-filter-rows (hooks/use-memo #(transport-filter-rows loads
                                                                      draft-transports
                                                                      ordered-draft-ids
                                                                      transport-summaries)
                                              [loads draft-transports ordered-draft-ids transport-summaries])
        transport-filter-field-specs (hooks/use-memo #(transport-field-specs transport-filter-rows)
                                                     [transport-filter-rows])
        filtered-transport-rows (hooks/use-memo #(filters/apply-filters transport-filter-rows
                                                                        transport-filter-field-specs
                                                                        active-transport-filters)
                                                [transport-filter-rows transport-filter-field-specs active-transport-filters])
        visible-transport-ids-set (hooks/use-memo #(into #{} (map :transport-id) filtered-transport-rows)
                                                  [filtered-transport-rows])
        all-available-vehicles (hooks/use-memo
                                #(filterv (fn [v]
                                            (and (not (contains? assigned-ids (:ductile/id v)))
                                                 (not (contains? staging-id-set (:ductile/id v)))))
                                          fahrzeuge)
                                [fahrzeuge assigned-ids staging-id-set])
        overview-map-fahrzeuge (hooks/use-memo
                                #(if show-fahrzeuge-on-transports?
                                   (filterv (fn [v]
                                              (not (contains? staging-id-set (:ductile/id v))))
                                            fahrzeuge)
                                   all-available-vehicles)
                                [show-fahrzeuge-on-transports? fahrzeuge all-available-vehicles staging-id-set])
        vehicles-available-cols (hooks/use-memo #(vehicles-available-columns fahrzeuge)
                                                [fahrzeuge])
        vehicles-table-opts-fn (hooks/use-memo
                                #(fn [opts]
                                   (vehicles-table-opts (assoc opts :available-cols vehicles-available-cols)))
                                [vehicles-available-cols])
        field-specs (hooks/use-memo #(vehicles-field-specs all-available-vehicles
                                                           vehicles-available-cols
                                                           (fn []
                                                             (when-some [the-map @overview-map/!the-map]
                                                               (mapbox/get-bounds the-map))))
                                    [all-available-vehicles vehicles-available-cols])
        field-spec-map (hooks/use-memo #(into {} (map (juxt ::filters/field identity) field-specs)) [field-specs])
        filtered-available (hooks/use-memo #(filters/apply-filters all-available-vehicles field-specs active-filters)
                                           [all-available-vehicles field-specs active-filters map-viewport-rev])
        available-vehicle-ids-set (hooks/use-memo #(into #{} (map :ductile/id all-available-vehicles))
                                                  [all-available-vehicles])
        selected-available-ids (hooks/use-memo #(vec (filter available-vehicle-ids-set selected-vehicle-ids))
                                               [available-vehicle-ids-set selected-vehicle-ids])
        vehicles-selection-weight-label (hooks/use-memo #(when (seq selected-available-ids)
                                                           (load-weight-label selected-available-ids vehicle-id->transport {:show-missing? true}))
                                                        [selected-available-ids vehicle-id->transport])
        vehicles-selection-height-label (hooks/use-memo #(when (seq selected-available-ids)
                                                           (max-height-label selected-available-ids vehicle-id->transport {:show-missing? true}))
                                                        [selected-available-ids vehicle-id->transport])
        selected-staging-ids (hooks/use-memo #(vec (filter (set staging-ids) selected-vehicle-ids))
                                             [staging-ids selected-vehicle-ids])
        staging-weight-label (hooks/use-memo #(load-weight-label (if (seq selected-staging-ids)
                                                                   selected-staging-ids
                                                                   staging-ids)
                                                                 vehicle-id->transport
                                                                 {:show-missing? (seq selected-staging-ids)})
                                             [selected-staging-ids staging-ids vehicle-id->transport])
        staging-height-label (hooks/use-memo #(max-height-label (if (seq selected-staging-ids)
                                                                  selected-staging-ids
                                                                  staging-ids)
                                                                vehicle-id->transport
                                                                {:show-missing? (seq selected-staging-ids)})
                                             [selected-staging-ids staging-ids vehicle-id->transport])
        current-view-snapshot {:col-settings col-settings
                               :filters serialized-active-filters}
        selected-view (selectors/current-view views selected-view-id)
        view-modified? (and selected-view
                            (not (selectors/snapshot-equal? current-view-snapshot view-baseline-snapshot)))
        view-modified-lines (when view-modified?
                              (modified-view-lines {:current-snapshot current-view-snapshot
                                                    :baseline-snapshot view-baseline-snapshot
                                                    :field-spec-map field-spec-map}))
        available-vehicles-count (count all-available-vehicles)
        visible-vehicles-count (count filtered-available)
        enforced-layout (update layout :hidden disj :vehicles)
        layout-focus-panel (:focus-panel layout)
        popped-out-ids (hooks/use-memo #(set (keys popouts)) [popouts])
        popout-panel (popup-panel-id)
        clear-focused-route! (fn []
                               (set! (.-current !route-request-token) (inc (.-current !route-request-token)))
                               (dispatch! {:event :route/hide}))
        resolve-current-transport-id (fn [transport-id]
                                       (let [wire-id (transport-id->wire transport-id)]
                                         (or (some (fn [existing-id]
                                                     (when (= (transport-id->wire existing-id) wire-id)
                                                       existing-id))
                                                   (keys (:transport->vehicle-ids @!state)))
                                             transport-id)))
        compute-focused-route!
        (fn [transport-id transport->vehicle-ids* transport->waypoint-settings*]
          (letfn [(request-route! [transport->vehicle-ids' transport->waypoint-settings' retries-left]
                    (let [resolved-id (resolve-current-transport-id transport-id)
                          token (inc (.-current !route-request-token))]
                      (log-lbv2! "compute-focused-route!/request"
                                 {:transport-id transport-id
                                  :resolved-id resolved-id
                                  :token token
                                  :retries-left retries-left
                                  :vehicle-count (count (get transport->vehicle-ids' resolved-id []))
                                  :focused-transport-id (:focused-transport-id @!state)})
                      (set! (.-current !route-request-token) token)
                      (dispatch! {:event :route/start
                                  :transport-id resolved-id})
                      (route-for-transport! resolved-id
                                            transport->vehicle-ids'
                                            vehicle-id->transport
                                            transport->waypoint-settings'
                                            (or (get-in @!state [:draft-transports resolved-id])
                                                (get transport-id->load resolved-id))
                                            (atom nil)
                                            (fn [{:keys [status route]}]
                                              (log-lbv2! "compute-focused-route!/result"
                                                         {:transport-id transport-id
                                                          :resolved-id resolved-id
                                                          :token token
                                                          :status status
                                                          :has-route? (boolean route)
                                                          :focused-transport-id (:focused-transport-id @!state)
                                                          :token-current (.-current !route-request-token)})
                                              (when (= token (.-current !route-request-token))
                                                (dispatch! {:event :route/result
                                                            :route route
                                                            :fit-key (when (= :ok status)
                                                                       (str resolved-id ":" token))})
                                                (when (and (= :empty status)
                                                           (pos? retries-left)
                                                           (= resolved-id (resolve-current-transport-id (:focused-transport-id @!state))))
                                                  (log-lbv2! "compute-focused-route!/retry-scheduled"
                                                             {:transport-id transport-id
                                                              :resolved-id resolved-id
                                                              :token token
                                                              :next-retries-left (dec retries-left)})
                                                  (js/setTimeout
                                                   (fn []
                                                     (request-route! (:transport->vehicle-ids @!state)
                                                                     (:transport->waypoint-settings @!state)
                                                                     (dec retries-left)))
                                                   180)))))))]
            (request-route! transport->vehicle-ids* transport->waypoint-settings* 2)))
        focus-created-draft-route! (fn [draft-id]
                                     (when draft-id
                                       (let [target-id (resolve-current-transport-id draft-id)]
                                         (log-lbv2! "focus-created-draft-route!/queued"
                                                    {:draft-id draft-id
                                                     :target-id target-id
                                                     :focused-transport-id (:focused-transport-id @!state)
                                                     :vehicle-count (count (get-in @!state [:transport->vehicle-ids target-id] []))})
                                         (set! (.-current !pending-created-draft-focus)
                                               {:draft-id target-id
                                                :requested-at (now-ms)})
                                         (dispatch! {:event :route/set-focused-id
                                                     :transport-id target-id}))))
        run-op! (fn [{:keys [op vehicle-ids transport-id name draft-id optimized-route? waypoint-order]}]
                  (log-lbv2! "run-op!"
                             {:op op
                              :transport-id transport-id
                              :draft-id draft-id
                              :vehicle-ids vehicle-ids})
                  (case op
                    :create-draft (do
                                    (dispatch! {:event :draft/create
                                                :draft-id draft-id
                                                :vehicle-ids (or vehicle-ids [])})
                                    (dispatch! {:event :transport/remove-from-staging
                                                :vehicle-ids (or vehicle-ids [])})
                                    (log-lbv2! "run-op!/create-draft-applied"
                                               {:draft-id draft-id
                                                :vehicle-count (count (get-in @!state [:transport->vehicle-ids draft-id] []))})
                                    (focus-created-draft-route! draft-id))
                    :move-to-transport (dispatch! {:event :transport/move-to-transport
                                                   :target-id transport-id
                                                   :vehicle-ids (or vehicle-ids [])})
                    :move-to-staging (dispatch! {:event :transport/move-to-staging
                                                 :vehicle-ids (or vehicle-ids [])})
                    :remove-from-load (remove-vehicles-from-load! {:transport-id transport-id
                                                                   :vehicle-ids (vec (or vehicle-ids []))
                                                                   :draft-ids draft-ids
                                                                   :popup-mode? popup-mode?
                                                                   :dispatch! dispatch!
                                                                   :compute-focused-route! compute-focused-route!
                                                                   :clear-focused-route! clear-focused-route!
                                                                   :state !state})
                    :remove-from-staging (dispatch! {:event :transport/remove-from-staging
                                                     :vehicle-ids (or vehicle-ids [])})
                    :create-draft-with-vehicles (do
                                                  (dispatch! {:event :draft/create-with-vehicles
                                                              :draft-id draft-id
                                                              :vehicle-ids (or vehicle-ids [])})
                                                  (focus-created-draft-route! draft-id))
                    :clear-staging (dispatch! {:event :transport/clear-staging})
                    :dismiss-draft (dispatch! {:event :draft/dismiss
                                               :transport-id transport-id})
                    :rename-draft (dispatch! {:event :draft/rename
                                              :transport-id transport-id
                                              :name name})
                    :set-waypoint-settings (do
                                             (dispatch! {:event :transport/set-waypoint-settings
                                                         :transport-id transport-id
                                                         :optimized-route? optimized-route?
                                                         :waypoint-order waypoint-order})
                                             (when (= transport-id (:focused-transport-id @!state))
                                               (compute-focused-route! transport-id
                                                                       (:transport->vehicle-ids @!state)
                                                                       (assoc (:transport->waypoint-settings @!state)
                                                                              transport-id
                                                                              {:optimized-route? (boolean optimized-route?)
                                                                               :waypoint-order (vec (or waypoint-order []))}))))
                    :focus-transport-route (when transport-id
                                             (compute-focused-route! transport-id
                                                                     (:transport->vehicle-ids @!state)
                                                                     (:transport->waypoint-settings @!state)))
                    :focus-selection-route (when (seq vehicle-ids)
                                             (dispatch! {:event :route/start-selection
                                                         :vehicle-ids (vec (distinct vehicle-ids))}))
                    :hide-focused-route (clear-focused-route!)
                    nil)
                  nil)
        prompt-view-name (fn [title default-value]
                           (some-> (js/prompt title default-value)
                                   selectors/normalize-view-name))
        save-view-as-new! (fn [default-name]
                            (when-let [name (prompt-view-name (str (tr "Save this view as") ":")
                                                              (or default-name (tr "Untitled")))]
                              (if (selectors/name-available? views name)
                                (dispatch! {:event :views/save-new
                                            :name name
                                            :snapshot current-view-snapshot})
                                (js/alert (tr "A view with this name already exists.")))))
        apply-view-snapshot! (fn [{:keys [col-settings filters]}]
                               (deframe/localstorage-assoc! vehicles-col-storage-key col-settings)
                               (deframe/route-assoc! vehicles-filters-key (vec (or filters []))))
        select-view! (fn [view-id]
                       (dispatch! {:event :views/select
                                   :selected-view-id view-id})
                       (when-let [view (selectors/current-view views view-id)]
                         (apply-view-snapshot! (:snapshot view))))
        {:keys [commands protocol]}
        (lbhooks/use-load-builder-v2-controller
         {:commands-args {:dispatch! dispatch!
                          :state !state
                          :loads loads
                          :draft-transports draft-transports
                          :active-filters active-filters
                          :vehicles-filters-key vehicles-filters-key
                          :vehicles-filters-draft-key vehicles-filters-draft-key
                          :vehicles-filters-modal-key vehicles-filters-modal-key
                          :field-spec-map field-spec-map
                          :vehicle-id->transport vehicle-id->transport
                          :cell->menu-filter-action cell->menu-filter-action
                          :compute-focused-route! compute-focused-route!
                          :clear-focused-route! clear-focused-route!}
          :protocol-args {:popup-mode? popup-mode?
                          :sid sid
                          :window-id window-id
                          :state !state
                          :dispatch! dispatch!
                          :now-ms now-ms
                          :normalize-op normalize-op
                          :normalize-message-type normalize-message-type
                          :resolve-vehicle-id resolve-vehicle-id
                          :transport-id->wire transport-id->wire
                          :vehicle-id->wire vehicle-id->wire
                          :make-op-message make-op-message
                          :post-message! post-message!
                          :post-op! post-op!
                          :ack-op! ack-op!
                          :valid-message? valid-message?
                          :push-trace! push-trace!
                          :ops-key ops-key
                          :!applied-op-ids !applied-op-ids
                          :!applied-op-order !applied-op-order
                          :!seen-message-ids !seen-message-ids
                          :!seen-message-order !seen-message-order
                          :run-op! run-op!
                          :on-server-snapshot! (fn [payload]
                                                 (when popup-mode?
                                                   (let [next-data (merge @!server-data
                                                                          (select-keys payload
                                                                                       [:fahrzeuge
                                                                                        :loads
                                                                                        :available-actions
                                                                                        :ductile.lambda/diff-basis-t]))]
                                                     (when (not= next-data @!server-data)
                                                       (reset! !server-data next-data)))))}})
        {:keys [submit-shared-op! handle-message! reconcile-from-shared! apply-shared-op!]} protocol
        {:keys [hide-focused-route!
                edit-transport!
                focus-transport-route!
                create-empty-draft!
                create-draft-from-staging!
                clear-staging!
                update-selection!
                update-drag-selection!
                preview-drag-selection!
                add-cell-filter!
                open-menu!]} commands
        focused-selection-fahrzeuge (hooks/use-memo #(vec (keep vehicle-id->transport focused-selection-vehicle-ids))
                                                    [vehicle-id->transport focused-selection-vehicle-ids])
        map-panel-fahrzeuge (if (seq focused-selection-vehicle-ids)
                              focused-selection-fahrzeuge
                              overview-map-fahrzeuge)
        map-destination->vehicle-ids (hooks/use-memo
                                      #(reduce (fn [acc vehicle]
                                                 (if-let [destination-id (map-destination-id vehicle)]
                                                   (update acc destination-id (fnil conj []) (:ductile/id vehicle))
                                                   acc))
                                               {}
                                               map-panel-fahrzeuge)
                                      [map-panel-fahrzeuge])
        add-map-viewport-filter! (fn []
                                   (when-let [bounds (when-some [the-map @overview-map/!the-map]
                                                       (mapbox/get-bounds the-map))]
                                     (let [filter-item (map-viewport-filter-item all-available-vehicles bounds)
                                           updated (upsert-map-viewport-filter active-filters filter-item)]
                                       (deframe/route-assoc! vehicles-filters-key (filters/serialize-filters updated)))))
        open-map-menu! (fn [^js event]
                         (let [open-empty-menu! (fn []
                                                  (dispatch! {:event :menu/set
                                                              :menu {:kind :map-empty
                                                                     :from :map
                                                                     :x (.-clientX event)
                                                                     :y (.-clientY event)
                                                                     :context-filter-action {:filter-item :map-viewport
                                                                                             :label (tr "Add filter: Map Viewport")}}}))
                               open-vehicles-menu! (fn [vehicle-ids]
                                                     (if (seq vehicle-ids)
                                                       (dispatch! {:event :menu/set
                                                                   :menu {:kind :map-pin
                                                                          :from :map
                                                                          :x (.-clientX event)
                                                                          :y (.-clientY event)
                                                                          :vehicle-ids (vec (distinct vehicle-ids))
                                                                          :source-transport-id nil}})
                                                       (open-empty-menu!)))
                               ^js the-map @overview-map/!the-map]
                           (if-not the-map
                             (open-empty-menu!)
                             (let [rect (.getBoundingClientRect (.getCanvas the-map))
                                   point #js [(- (.-clientX event) (.-left rect))
                                              (- (.-clientY event) (.-top rect))]
                                   marker-features (.queryRenderedFeatures the-map point
                                                                           (j/lit {:layers ["unclusteredLocationsDelivery"]}))
                                   cluster-features (.queryRenderedFeatures the-map point
                                                                            (j/lit {:layers ["locationClustersDelivery"]}))
                                   marker-id (j/get-in marker-features [0 :properties :id])
                                   cluster-id (j/get-in cluster-features [0 :properties :cluster_id])
                                   cluster-count (j/get-in cluster-features [0 :properties :point_count])
                                   ^js cluster-source (.getSource the-map "locationClusters")]
                               (cond
                                 marker-id
                                 (open-vehicles-menu! (get map-destination->vehicle-ids marker-id []))

                                 (and cluster-id cluster-count)
                                 (.getClusterLeaves cluster-source cluster-id cluster-count 0
                                                    (fn [err features]
                                                      (if err
                                                        (open-empty-menu!)
                                                        (let [ids (->> (js->clj features :keywordize-keys true)
                                                                       (map #(get-in % [:properties :id]))
                                                                       (mapcat #(get map-destination->vehicle-ids % []))
                                                                       distinct
                                                                       vec)]
                                                          (open-vehicles-menu! ids)))))

                                 :else
                                 (open-empty-menu!))))))
        focused-summary (get transport-summaries focused-transport-id)
        loading-summary (get transport-summaries route-loading-transport-id)
        focused-waypoints (:waypoints focused-summary)
        focused-title (when focused-transport-id
                        (if (contains? draft-ids focused-transport-id)
                          (or (get-in draft-transports [focused-transport-id :draft/name])
                              (str (tr "Draft transport") " " (or (get draft-index-by-id focused-transport-id) "?")))
                          (or (get-in transport-id->load [focused-transport-id :load/id])
                              (:load-id focused-summary)
                              (tr "Transport"))))
        focused-header-text (when focused-transport-id
                              (if (seq focused-waypoints)
                                (str focused-title " · " (str/join " → " (map panels/waypoint-label focused-waypoints)))
                                focused-title))
        selection-header-text (when (seq focused-selection-vehicle-ids)
                                (tr "Selection · %1 vehicles" (count focused-selection-vehicle-ids)))
        route-loading-label (when route-loading-transport-id
                              (or (get-in transport-id->load [route-loading-transport-id :load/id])
                                  (get-in draft-transports [route-loading-transport-id :draft/name])
                                  (:load-id loading-summary)))
        transports-count (+ (count loads) (count draft-transports))
        visible-transports-count (count filtered-transport-rows)
        panel-meta {:vehicles {:title [:span.inline-flex.items-center.gap-1
                                       [ui/icon {:size 12} :car]
                                       [:span (get-in panel-titles [:vehicles :title])]]
                               :popout-enabled? false
                               :header-right
                               (let [parts (remove nil?
                                                   [(when (seq vehicles-selection-height-label)
                                                      vehicles-selection-height-label)
                                                    (when (seq vehicles-selection-weight-label)
                                                      vehicles-selection-weight-label)
                                                    (tr "%1/%2 available vehicles"
                                                        visible-vehicles-count
                                                        available-vehicles-count)])]
                                 (into [:span.inline-flex.items-center.gap-2.text-slate-600
                                        {:data-testid "vehicles-summary"}]
                                       (map-indexed (fn [idx part]
                                                      ^{:key (str "vehicles-summary-part-" idx)}
                                                      [:span part])
                                                    parts)))}
                    :staging {:title [:span.inline-flex.items-center.gap-1
                                      [ui/icon {:size 12} :clipboard]
                                      [:span (get-in panel-titles [:staging :title])]]
                              :header-right [:<>
                                             (let [parts (remove nil?
                                                                 [(when (seq staging-height-label)
                                                                    staging-height-label)
                                                                  (when (seq staging-weight-label)
                                                                    staging-weight-label)
                                                                  (tr "%1 vehicles" (count staging-ids))])]
                                               (into [:span.inline-flex.items-center.gap-2.text-slate-600
                                                      {:data-testid "staging-summary"}]
                                                     (map-indexed (fn [idx part]
                                                                    ^{:key (str "staging-summary-part-" idx)}
                                                                    [:span part])
                                                                  parts)))
                                             (when (seq staging-ids)
                                               [panels/header-link-button {:on-click create-draft-from-staging!
                                                                           :testid "staging-new-draft-from-staging"
                                                                           :label (tr "New draft")}])
                                             (when (seq staging-ids)
                                               [panels/header-link-button {:on-click clear-staging!
                                                                           :testid "staging-clear"
                                                                           :label (tr "Clear")}])]}
                    :transports {:title [:span.inline-flex.items-center.gap-1
                                         [ui/icon {:size 12} :truck]
                                         [:span (get-in panel-titles [:transports :title])]]
                                 :header-right [:<>
                                                [:span.text-slate-600 {:data-testid "transports-count"}
                                                 (if (seq active-transport-filters)
                                                   (tr "%1/%2 transports" visible-transports-count transports-count)
                                                   (tr "%1 transports" transports-count))]
                                                [panels/header-link-button {:on-click #(deframe/route-assoc! transports-filters-open-key
                                                                                                             (not transport-filters-open?))
                                                                            :testid "transports-filters-toggle"
                                                                            :label (cond
                                                                                     transport-filters-open?
                                                                                     (tr "Hide filters")

                                                                                     (seq active-transport-filters)
                                                                                     (tr "Filters (%1)" (count active-transport-filters))

                                                                                     :else
                                                                                     (tr "Filters"))}]
                                                [panels/header-link-button {:on-click create-empty-draft!
                                                                            :testid "transports-new-draft"
                                                                            :label (tr "New draft")}]]}
                    :map {:title [:span.inline-flex.items-center.gap-1
                                  [ui/icon {:size 12} :map]
                                  [:span (get-in panel-titles [:map :title])]]
                          :header-right
                          [:div.flex.items-center.gap-2.min-w-0
                           [:span.text-slate-600.min-w-0.truncate {:data-testid "map-header-text"}
                            (if (or focused-transport-id (seq focused-selection-vehicle-ids))
                              (or focused-header-text selection-header-text)
                              (tr "Overview map"))]
                           (when (or focused-transport-id (seq focused-selection-vehicle-ids))
                             [panels/header-link-button {:on-click hide-focused-route!
                                                         :testid "map-hide-route"
                                                         :label (if (seq focused-selection-vehicle-ids)
                                                                  (tr "Hide selection")
                                                                  (tr "Hide this route"))}])]}}
        panel->view
        (fn [panel-id]
          (case panel-id
            :vehicles
            [panels/vehicles-panel {:fahrzeuge fahrzeuge
                                    :assigned-ids assigned-ids
                                    :staging-ids staging-id-set
                                    :selected-vehicle-ids selected-vehicle-ids
                                    :col-settings col-settings
                                    :active-filters active-filters
                                    :field-specs field-specs
                                    :show-cols-modal? show-cols-modal?
                                    :on-close-cols-modal #(dispatch! {:event :ui/set-show-cols-modal :show? false})
                                    :on-open-cols-modal #(dispatch! {:event :ui/set-show-cols-modal :show? true})
                                    :on-change-col-settings #(deframe/localstorage-assoc! vehicles-col-storage-key %)
                                    :on-open-filters-modal #(do
                                                              (deframe/route-assoc! vehicles-filters-draft-key active-filters)
                                                              (deframe/route-assoc! vehicles-filters-modal-key true))
                                    :on-open-filter-for-field (fn [field]
                                                                (when-let [field-spec (get field-spec-map field)]
                                                                  (let [field-type (::filters/type field-spec)
                                                                        operator (filters/default-operator-for-spec field-spec)
                                                                        draft-filter {::filters/id (random-uuid)
                                                                                      ::filters/field field
                                                                                      ::filters/operator operator
                                                                                      ::filters/value (if (= :boolean field-type) false "")
                                                                                      ::filters/label nil}]
                                                                    (deframe/route-assoc! vehicles-filters-draft-key
                                                                                          (conj (vec active-filters) draft-filter))
                                                                    (deframe/route-assoc! vehicles-filters-modal-key true))))
                                    :on-remove-filter (fn [filter-id]
                                                        (deframe/route-assoc! vehicles-filters-key
                                                                              (->> active-filters
                                                                                   (remove #(= filter-id (::filters/id %)))
                                                                                   filters/serialize-filters)))
                                    :on-select-row update-selection!
                                    :on-row-double-click (fn [event vehicle-id]
                                                           (.preventDefault event)
                                                           (.stopPropagation event)
                                                           (log-lbv2! "vehicles/double-click"
                                                                      {:vehicle-id vehicle-id
                                                                       :latest-draft-id (first ordered-draft-ids)
                                                                       :ordered-draft-ids ordered-draft-ids})
                                                           (if-let [latest-draft-id (first ordered-draft-ids)]
                                                             (submit-shared-op! {:op :move-to-transport
                                                                                 :transport-id latest-draft-id
                                                                                 :vehicle-ids [vehicle-id]})
                                                             (submit-shared-op! {:op :create-draft
                                                                                 :vehicle-ids [vehicle-id]})))
                                    :on-drag-select update-drag-selection!
                                    :on-drag-select-preview preview-drag-selection!
                                    :on-open-assign-menu (fn [event vehicle-id]
                                                           (open-menu! {:event event
                                                                        :vehicle-id vehicle-id
                                                                        :source-transport-id nil
                                                                        :from :vehicles
                                                                        :visible-vehicle-ids (mapv :ductile/id filtered-available)}))
                                    :table-opts-fn vehicles-table-opts-fn
                                    :filters-key vehicles-filters-key
                                    :available-cols vehicles-available-cols
                                    :column-order default-vehicles-column-order}]

            :transports
            [panels/transports-panel {:loads loads
                                      :draft-transports draft-transports
                                      :draft-order draft-order
                                      :active-filters active-transport-filters
                                      :filters-open? transport-filters-open?
                                      :filter-field-specs transport-filter-field-specs
                                      :filter-rows transport-filter-rows
                                      :visible-transport-ids visible-transport-ids-set
                                      :transport->vehicle-ids transport->vehicle-ids
                                      :transport->waypoint-settings transport->waypoint-settings
                                      :transport-update-tokens transport-update-tokens
                                      :vehicle-id->transport vehicle-id->transport
                                      :selected-vehicle-ids selected-vehicle-ids
                                      :on-select-row update-selection!
                                      :on-drag-select update-drag-selection!
                                      :on-drag-select-preview preview-drag-selection!
                                      :col-settings col-settings
                                      :focused-transport-id focused-transport-id
                                      :on-focus-map focus-transport-route!
                                      :on-hide-map hide-focused-route!
                                      :on-edit edit-transport!
                                      :on-save-draft edit-transport!
                                      :on-rename-draft (fn [transport-id name]
                                                         (submit-shared-op! {:op :rename-draft
                                                                             :transport-id transport-id
                                                                             :name name}))
                                      :on-open-transport-menu (fn [event transport-menu]
                                                                (dispatch! {:event :menu/set
                                                                            :menu (assoc transport-menu
                                                                                         :x (.-clientX event)
                                                                                         :y (.-clientY event)
                                                                                         :kind :transport-card)}))
                                      :on-save-waypoint-settings (fn [transport-id {:keys [optimized-route? waypoint-order]}]
                                                                   (submit-shared-op! {:op :set-waypoint-settings
                                                                                       :transport-id transport-id
                                                                                       :optimized-route? optimized-route?
                                                                                       :waypoint-order waypoint-order}))
                                      :optimize-waypoints! (fn [transport-id waypoints]
                                                             (let [vehicle-ids (get transport->vehicle-ids transport-id [])
                                                                   transports (vec (keep vehicle-id->transport vehicle-ids))
                                                                   origin (or (some :transport/origin transports)
                                                                              (pick-route-origin vehicle-ids vehicle-id->transport))]
                                                               (optimize-waypoints! {:origin origin
                                                                                     :waypoints waypoints})))
                                      :waypoint-key-fn selectors/waypoint-key
                                      :load-weight-label-fn #(load-weight-label % vehicle-id->transport {:show-missing? false})
                                      :on-dismiss-draft (fn [transport-id]
                                                          (submit-shared-op! {:op :dismiss-draft
                                                                              :transport-id transport-id}))
                                      :on-toggle-filters-open #(deframe/route-assoc! transports-filters-open-key %)
                                      :on-change-filters #(deframe/route-assoc! transports-filters-key
                                                                                (filters/serialize-filters %))
                                      :on-right-click-vehicle (fn [event vehicle-id source-transport-id]
                                                                (open-menu! {:event event
                                                                             :vehicle-id vehicle-id
                                                                             :source-transport-id source-transport-id
                                                                             :from :transport
                                                                             :visible-vehicle-ids (get transport->vehicle-ids source-transport-id [])}))
                                      :table-opts-fn vehicles-table-opts-fn}]

            :staging
            [panels/staging-panel {:staging-ids staging-ids
                                   :vehicle-id->transport vehicle-id->transport
                                   :selected-vehicle-ids selected-vehicle-ids
                                   :on-select-row update-selection!
                                   :on-drag-select update-drag-selection!
                                   :on-drag-select-preview preview-drag-selection!
                                   :col-settings col-settings
                                   :on-remove-from-staging #(submit-shared-op! {:op :remove-from-staging
                                                                                :vehicle-ids [%]})
                                   :on-open-assign-menu (fn [event vehicle-id]
                                                          (open-menu! {:event event
                                                                       :vehicle-id vehicle-id
                                                                       :source-transport-id nil
                                                                       :from :staging
                                                                       :visible-vehicle-ids staging-ids}))
                                   :table-opts-fn vehicles-table-opts-fn}]

            :map
            [panels/map-panel {:fahrzeuge map-panel-fahrzeuge
                               :focused-route (when-not (seq focused-selection-vehicle-ids) focused-route)
                               :fit-route-key (when-not (seq focused-selection-vehicle-ids) focused-route-fit-key)
                               :route-loading? route-loading?
                               :route-loading-label route-loading-label
                               :on-viewport-change #(swap! !map-viewport-rev inc)
                               :on-context-menu open-map-menu!}]

            [:div]))]
    (hooks/use-effect
     (fn []
       (when popup-mode?
         (let [next-data {:fahrzeuge raw-fahrzeuge
                          :loads raw-loads
                          :available-actions raw-available-actions
                          :ductile.lambda/diff-basis-t raw-diff-basis-t}]
           (when (not= next-data @!server-data)
             (reset! !server-data next-data)))))
     [popup-mode? raw-fahrzeuge raw-loads raw-available-actions raw-diff-basis-t])
    (hooks/use-effect
     (fn []
       (when-not popup-mode?
         (broadcast-message! sid
                             (vals popouts)
                             :server/snapshot
                             {:fahrzeuge raw-fahrzeuge
                              :loads raw-loads
                              :available-actions raw-available-actions
                              :ductile.lambda/diff-basis-t raw-diff-basis-t})))
     [popup-mode? sid popouts raw-fahrzeuge raw-loads raw-available-actions raw-diff-basis-t])
    (hooks/use-effect
     (fn []
       (when-not popup-mode?
         (reset! !last-update-ms (now-ms))))
     [popup-mode? raw-fahrzeuge raw-loads raw-available-actions raw-diff-basis-t])
    (hooks/use-effect
     (fn []
       (when-not popup-mode?
         (when-let [selected-snapshot
                    (some-> (selectors/current-view (:views persisted-views-state)
                                                    (:selected-view-id persisted-views-state))
                            :snapshot)]
           (apply-view-snapshot! selected-snapshot))
         (dispatch! {:event :views/init
                     :views-state persisted-views-state})
         (set! (.-current !views-hydrated?) true)))
     [])
    (hooks/use-effect
     (fn []
       (when (and (not popup-mode?)
                  (.-current !views-hydrated?))
         (deframe/localstorage-assoc! views-storage-key
                                      {:views views
                                       :selected-view-id selected-view-id})))
     [popup-mode? views selected-view-id])
    (effects/use-popout-cleanup! {:popup-mode? popup-mode?
                                  :dispatch! dispatch!})
    (effects/use-message-listener! {:sid sid
                                    :popup-mode? popup-mode?
                                    :popout-panel popout-panel
                                    :transport->vehicle-ids transport->vehicle-ids
                                    :popouts popouts
                                    :vehicle-id->transport vehicle-id->transport
                                    :handle-message! handle-message!})
    (effects/use-popup-pending-ops! {:popup-mode? popup-mode?
                                     :sid sid
                                     :window-id window-id
                                     :state !state
                                     :dispatch! dispatch!
                                     :now-ms now-ms
                                     :enqueue-popup-op! (fn [op]
                                                          (protocol/enqueue-popup-op!
                                                           {:ops-key ops-key :sid sid :now-ms now-ms}
                                                           op))
                                     :push-trace! push-trace!})
    (effects/use-popup-pending-gc! {:popup-mode? popup-mode?
                                    :dispatch! dispatch!
                                    :now-ms now-ms})
    (effects/use-main-ops-drain! {:popup-mode? popup-mode?
                                  :sid sid
                                  :state !state
                                  :window-id window-id
                                  :transport-id->wire transport-id->wire
                                  :apply-shared-op! apply-shared-op!
                                  :post-op! post-op!
                                  :popouts popouts
                                  :now-ms now-ms
                                  :ops-key ops-key
                                  :push-trace! push-trace!})
    (effects/use-loads-sync! {:loads loads
                              :fahrzeuge fahrzeuge
                              :draft-transports draft-transports
                              :dispatch! dispatch!})
    (hooks/use-effect
     (fn []
       (when-let [{:keys [draft-id requested-at]} (.-current !pending-created-draft-focus)]
         (let [target-id (resolve-current-transport-id draft-id)
               vehicle-count (count (get transport->vehicle-ids target-id []))
               age-ms (- (now-ms) requested-at)]
           (log-lbv2! "focus-created-draft-route!/pending-check"
                      {:draft-id draft-id
                       :target-id target-id
                       :focused-transport-id focused-transport-id
                       :vehicle-count vehicle-count
                       :age-ms age-ms})
           (when (or (pos? vehicle-count)
                     (> age-ms 5000))
             (set! (.-current !pending-created-draft-focus) nil)
             (compute-focused-route! target-id
                                     transport->vehicle-ids
                                     transport->waypoint-settings)))))
     [transport->vehicle-ids
      transport->waypoint-settings
      focused-transport-id])
    (effects/use-layout-persistence! {:layout layout})
    (effects/use-enforce-vehicles-panel! {:dispatch! dispatch!})
    (effects/use-map-resize-on-layout! {:layout layout
                                        :popouts popouts})
    (effects/use-heartbeat! {:popup-mode? popup-mode?
                             :sid sid
                             :heartbeat-key heartbeat-key
                             :now-ms now-ms})
    (effects/use-shared-state-sync! {:popup-mode? popup-mode?
                                     :sid sid
                                     :layout layout
                                     :draft-transports draft-transports
                                     :draft-order draft-order
                                     :transport->vehicle-ids transport->vehicle-ids
                                     :transport->waypoint-settings transport->waypoint-settings
                                     :staging-ids staging-ids
                                     :focused-transport-id focused-transport-id
                                     :shared-state-key shared-state-key
                                     :heartbeat-key heartbeat-key
                                     :now-ms now-ms
                                     :state !state
                                     :!last-shared-focused-id !last-shared-focused-id
                                     :dispatch! dispatch!
                                     :popout-panel popout-panel
                                     :compute-focused-route! compute-focused-route!
                                     :clear-focused-route! clear-focused-route!
                                     :reconcile-from-shared! reconcile-from-shared!})
    (effects/use-escape-reset! {:dispatch! dispatch!})

    [:<>
     [panels/render-load-builder-v2
      {:popup-mode? popup-mode?
       :disconnected? disconnected?
       :popout-panel popout-panel
       :panel-meta panel-meta
       :panel->view panel->view
       :col-settings col-settings
       :on-change-col-settings #(deframe/localstorage-assoc! vehicles-col-storage-key %)
       :on-open-cols-modal #(dispatch! {:event :ui/set-show-cols-modal :show? true})
       :views views
       :selected-view-id selected-view-id
       :view-modified? view-modified?
       :view-modified-lines view-modified-lines
       :on-select-view select-view!
       :on-save-view #(save-view-as-new! (or (some-> selected-view :name) (tr "Untitled")))
       :on-rename-view (fn []
                         (when-let [view selected-view]
                           (when-let [name (prompt-view-name (str (tr "Rename \"%1\"" (:name view)) ":")
                                                             (:name view))]
                             (if (selectors/name-available? views name (:id view))
                               (dispatch! {:event :views/rename
                                           :view-id (:id view)
                                           :name name})
                               (js/alert (tr "A view with this name already exists."))))))
       :on-duplicate-view (fn []
                            (when-let [view selected-view]
                              (when-let [name (prompt-view-name (str (tr "Duplicate \"%1\" as" (:name view)) ":")
                                                                (str (:name view) " 2"))]
                                (if (= (str/lower-case name) (str/lower-case (:name view)))
                                  (js/alert (tr "The duplicate name must be different from the original."))
                                  (if (selectors/name-available? views name)
                                    (dispatch! {:event :views/duplicate
                                                :source-id (:id view)
                                                :name name})
                                    (js/alert (tr "A view with this name already exists.")))))))
       :on-delete-view (fn []
                         (when-let [view selected-view]
                           (when (js/confirm (tr "Delete \"%1\"?" (:name view)))
                             (dispatch! {:event :views/delete
                                         :view-id (:id view)}))))
       :on-save-view-changes #(dispatch! {:event :views/save-current
                                          :snapshot current-view-snapshot})
       :on-save-view-as-new #(save-view-as-new! (str (or (some-> selected-view :name) (tr "Untitled")) " 2"))
       :on-discard-view-changes (fn []
                                  (when-let [view selected-view]
                                    (apply-view-snapshot! (:snapshot view))
                                    (dispatch! {:event :views/discard-changes})))
       :show-fahrzeuge-on-transports? show-fahrzeuge-on-transports?
       :on-toggle-show-fahrzeuge-on-transports! #(deframe/localstorage-assoc! storage-key
                                                                              (not show-fahrzeuge-on-transports?))
       :layout-focus-panel layout-focus-panel
       :popouts popouts
       :dispatch! dispatch!
       :panel-ids panel-ids
       :panel-titles panel-titles
       :popped-out-ids popped-out-ids
       :enforced-layout enforced-layout
       :sid sid
       :popup-url popup-url
       :menu menu
       :all-transport-ids all-transport-ids
       :draft-ids draft-ids
       :draft-order draft-order
       :transport-summaries transport-summaries
       :add-cell-filter! add-cell-filter!
       :add-map-viewport-filter! add-map-viewport-filter!
       :on-copy-vins! (fn [vehicle-ids]
                        (let [vins (->> vehicle-ids
                                        (keep #(get-in vehicle-id->transport [% :vehicle/vin]))
                                        distinct
                                        vec)]
                          (when (seq vins)
                            (when-let [clipboard (.-clipboard js/navigator)]
                              (.writeText clipboard (str/join "," vins))))))
       :on-open-vehicle-details! (fn [vehicle-id]
                                   (when-let [vehicle (get vehicle-id->transport vehicle-id)]
                                     (let [href (routes/path '[compound :compound-name vehicle :fahrzeug-id]
                                                             {:compound-name (or (:compound/name vehicle)
                                                                                 (:compound/name (deframe/current-route-params)))
                                                              :fahrzeug-id (:ductile/id vehicle)})]
                                       (js/window.open href "_blank"))))
       :on-edit-transport-menu! edit-transport!
       :on-save-draft-menu! edit-transport!
       :on-dismiss-draft-menu! (fn [transport-id]
                                 (submit-shared-op! {:op :dismiss-draft
                                                     :transport-id transport-id}))
       :on-print-load-list-menu! (fn [transport-id]
                                   (when-let [transport (get transport-id->entity transport-id)]
                                     (js/window.open (transport/transport-link 'load-list transport)
                                                     "_blank")))
       :on-focus-transport-map-menu! focus-transport-route!
       :on-hide-transport-map-menu! (fn [_transport-id]
                                      (hide-focused-route!))
       :on-request-remove-from-load! (fn [transport-id vehicle-ids]
                                       (let [vehicle-ids (vec vehicle-ids)
                                             vehicles (->> vehicle-ids
                                                           (keep (fn [vehicle-id]
                                                                   (when-let [vehicle (get vehicle-id->transport vehicle-id)]
                                                                     {:vin (:vehicle/vin vehicle)
                                                                      :model (:vehicle/model vehicle)})))
                                                           vec)
                                             {:keys [load-id]}
                                             (get transport-summaries transport-id {})]
                                         (reset! !remove-from-load-confirmation
                                                 {:transport-id transport-id
                                                  :vehicle-ids vehicle-ids
                                                  :vehicles vehicles
                                                  :load-id load-id})))
       :submit-shared-op! submit-shared-op!
       :editing-transport-id editing-transport-id
       :show-edit-transport-modal? show-edit-transport-modal?
       :draft-id-set draft-ids
       :transport->vehicle-ids transport->vehicle-ids
       :vehicle-id->transport vehicle-id->transport
       :available-actions available-actions
       :on-finish-edit (fn [transport-id load]
                         (submit-transport! {:transport-id transport-id
                                             :draft? (contains? draft-ids transport-id)
                                             :load load
                                             :transport->vehicle-ids transport->vehicle-ids
                                             :vehicle-id->transport vehicle-id->transport
                                             :transport->waypoint-settings transport->waypoint-settings
                                             :on-success (fn [{:keys [transport-id draft?]}]
                                                           (when draft?
                                                             (submit-shared-op! {:op :dismiss-draft
                                                                                 :transport-id transport-id}))
                                                           (dispatch! {:event :ui/set-editing-transport
                                                                       :transport-id nil})
                                                           (deframe/route-dissoc! ::transport/edit-transport)
                                                           (deframe/route-dissoc! :current-load))}))
       :filters-modal-open? filters-modal-open?
       :visible-vehicles-count visible-vehicles-count
       :available-vehicles-count available-vehicles-count
       :field-specs field-specs
       :all-available-vehicles all-available-vehicles
       :draft-filters draft-filters
       :vehicles-filters-key vehicles-filters-key
       :vehicles-filters-draft-key vehicles-filters-draft-key
       :vehicles-filters-modal-key vehicles-filters-modal-key
       :on-filters-change #(deframe/route-assoc! vehicles-filters-draft-key %)
       :on-filters-cancel #(do
                             (deframe/route-dissoc! vehicles-filters-modal-key)
                             (deframe/route-dissoc! vehicles-filters-draft-key))
       :on-filters-reset #(deframe/route-assoc! vehicles-filters-draft-key [])
       :on-filters-apply (fn [new-filters]
                           (deframe/route-assoc! vehicles-filters-key (filters/serialize-filters new-filters))
                           (deframe/route-dissoc! vehicles-filters-modal-key)
                           (deframe/route-dissoc! vehicles-filters-draft-key))
       :vehicles-table-opts-fn vehicles-table-opts-fn
       :last-update-ms @!last-update-ms
       :auto-refresh-seconds auto-refresh-seconds}]
     (when-let [{:keys [transport-id vehicle-ids vehicles load-id]} @!remove-from-load-confirmation]
       (let [close! #(reset! !remove-from-load-confirmation nil)
             vehicle-count (count vehicle-ids)
             load-label (or load-id (tr "No load ID"))
             single-vehicle-label (when (= 1 vehicle-count)
                                    (let [{:keys [vin model]} (first vehicles)]
                                      (str (or model "") " (" vin ")")))]
         [ui/alert {:open? true :on-close close!}
          [ui/alert-content
           [ui/alert-title
            (if (= 1 vehicle-count)
              (tr "Remove 1 vehicle from load")
              (tr "Remove %1 vehicles from load" vehicle-count))]
           [ui/alert-description
            [:div.text-xs
             (if (= 1 vehicle-count)
               [:div (tr "%1 will be removed from %2." single-vehicle-label load-label)]
               [:<>
                [:div.mb-2 (tr "The following vehicles will be removed from %1:" load-label)]
                [:ul.list-disc.pl-5.space-y-1
                 (for [{:keys [vin model]} vehicles]
                   ^{:key vin}
                   [:li
                    [:span.font-medium (str (or model "") " (" vin ")")]])]])]]
           [:div.flex.justify-end.gap-3
            [ui/alert-cancel
             [ui/button {:on-click close!
                         :data-testid "remove-from-load-cancel"}
              (tr "Cancel")]]
            [ui/alert-action
             [ui/button {:variant :destructive
                         :data-testid "remove-from-load-confirm"
                         :on-click #(do
                                      (close!)
                                      (when (contains? draft-ids transport-id)
                                        (deframe/tx! (show-remove-from-load-toast-tx vehicle-count)))
                                      (submit-shared-op! {:op :remove-from-load
                                                          :transport-id transport-id
                                                          :vehicle-ids vehicle-ids}))}
              (if (= 1 vehicle-count)
                (tr "Remove from load")
                (tr "Remove %1 vehicles from load" vehicle-count))]]]]]))]))

(defn view [_]
  [load-builder-v2 @(deframe/!route-get-in [:fetch :result] {})])

(defn popup-view [_]
  [load-builder-v2 @(deframe/!route-get-in [:fetch :result] {})])
